import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// @mui material components
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "../../../components/MDBox";
import MDButton from "../../../components/MDButton";
import MDTypography from "../../../components/MDTypography";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "../../../shared/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../shared/Navbars/DashboardNavbar";
import Footer from "../../../shared/Footer";

// NewProduct page components
import InstitutionInfo from "./components/InstitutionInfo";
import institutionService from "../../../services/institutionService";

// Esquema de validación con Yup
const InstitutionSchema = yup.object().shape({
  name: yup.string().required("El nombre es obligatorio").min(3, "Mínimo 3 caracteres"),
  description: yup.string().required("La descripción es obligatoria"),
  website: yup.string(),
  logo: yup.mixed().required("El logo es obligatorio")
});

const steps = ["1. Sobre la institución"];

function NewInstitution() {
  let navigate = useNavigate();
  const { id } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isLastStep = activeStep === steps.length - 1;

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(InstitutionSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      website: "",
      logo: ""
    },
  });

  useEffect(() => {
    const fetchInstitution = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const { data } = await institutionService.getById(id);
        const institution = data;

        Object.keys(institution).forEach((key) => {
          const value = institution[key] || "";
          setValue(key, value);
        });
      } catch (error) {
        console.error("Error cargando institución:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInstitution();
  }, [id, setValue]);

  const handleNext = async () => {
    const fieldsToValidate =
      activeStep === 0
        ? ["name", "description", "website", "logo"]
        : [];

    const result = await trigger(fieldsToValidate);

    if (result) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep(activeStep - 1);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (!id) {
        await institutionService.create(data)
      } else {
        await institutionService.update(id, data)
      }
      navigate("/institutions");
    } catch (error) {
      console.error("Error guardando la institución:", error);
      const errorMessage = error.response?.data?.error || "Error al guardar.";
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <MDBox mt={6} mb={8} textAlign="center">
              <MDTypography variant="h3" fontWeight="bold">
                {id ? "Editar" : "Registrar nueva"} institución
              </MDTypography>
              <MDTypography variant="h5" color="secondary">
                Información de la institución
              </MDTypography>
            </MDBox>

            <Card>
              <MDBox mt={-3} mb={3} mx={2}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </MDBox>

              <MDBox p={3}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {activeStep === 0 && (
                    <InstitutionInfo control={control} errors={errors} />
                  )}

                  <MDBox mt={4} display="flex" justifyContent="space-between">
                    {activeStep === 0 ? (
                      <MDBox />
                    ) : (
                      <MDButton variant="gradient" color="light" onClick={handleBack}>
                        Atrás
                      </MDButton>
                    )}

                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={isLastStep ? handleSubmit(onSubmit) : handleNext}
                      disabled={isLoading}
                    >
                      {isLoading ? "Guardando..." : isLastStep ? "Guardar" : "Siguiente"}
                    </MDButton>
                  </MDBox>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NewInstitution;
